import React, { useState, useRef, useEffect } from 'react';
import './Terminal.css';
import { commandMap } from '../commands';
import connect from '../commands/connect';
import disconnect from '../commands/disconnect';
import miningCommands from '../commands/mining';
import clear from '../commands/clear';
import balance from '../commands/balance';
import restore from '../commands/restore';
import { getAIResponse } from '../utils/AIService';

const Terminal: React.FC = () => {
  const [input, setInput] = useState('');
  const [history, setHistory] = useState<string[]>([
    "AI CRYPTO-ASSISTANT v1.0",
    "Welcome to the most advanced AI-powered Crypto Mining & Staking Assistant.",
    "Type 'help' for a list of available commands.",
    "Mine or stake cryptocurrencies across Solana & EVM chains to earn rewards.",
  ]);
  const inputRef = useRef<HTMLInputElement>(null);
  const historyRef = useRef<HTMLDivElement>(null);

  const [connectedNetwork, setConnectedNetwork] = useState<'solana' | 'ethereum' | 'base' | 'arbitrum' | 'bnb' | 'optimism' | null>(null);

  const scrollToBottom = () => {
    historyRef.current?.scrollTo(0, historyRef.current.scrollHeight);
  };

  useEffect(() => {
    scrollToBottom();
  }, [history]);

  const runCommand = async (cmd: string) => {
    const lower = cmd.toLowerCase();

    if (lower.startsWith('connect')) {
      const network = cmd.split(' ')[1]?.toLowerCase();

      if (connectedNetwork) {
        setHistory(prev => [...prev, `Already connected to a ${connectedNetwork} wallet.`]);
        return;
      }

      const validNetworks = ['solana', 'ethereum', 'base', 'arbitrum', 'bnb', 'optimism'];
      if (validNetworks.includes(network)) {
        const result = await connect(network as any, setConnectedNetwork);
        if (result[0].includes('successfully')) {
          setConnectedNetwork(network as any);
        }
        setHistory(prev => [...prev, `> ${cmd}`, ...result]);
      } else {
        setHistory(prev => [...prev, `> ${cmd}`, `Unsupported network.`]);
      }

    } else if (lower === 'disconnect') {
      if (!connectedNetwork) {
        setHistory(prev => [...prev, `> ${cmd}`, 'No wallet is connected.']);
        return;
      }

      const result = await disconnect(connectedNetwork, setHistory, setConnectedNetwork);
      setHistory(prev => [...prev, `> ${cmd}`, ...result]);

    } else if (lower === 'balance') {
      const result = await balance(connectedNetwork);
      setHistory(prev => [...prev, `> ${cmd}`, ...result]);

    } else if (lower.startsWith('mining')) {
      const miningCommand = cmd.split(' ')[1]?.toLowerCase();
      if (miningCommand) {
        if (!connectedNetwork) {
          setHistory(prev => [...prev, `> ${cmd}`, 'No wallet connected.']);
          return;
        }
        const miningResult = await miningCommands(miningCommand, setHistory, connectedNetwork);
        setHistory(prev => [...prev, `> ${cmd}`, ...miningResult]);
      } else {
        setHistory(prev => [...prev, `> ${cmd}`, 'Invalid mining command.']);
      }

    } else if (lower === 'clear') {
      clear("", setHistory);
      return;

    } else if (lower.startsWith('restore')) {
      const privateKey = cmd.replace('restore ', '').trim();
      const messages = await restore(privateKey, setHistory);
      setHistory(prev => [...prev, ...messages]);
      return;

    } else if (commandMap[lower]) {
      const result = await commandMap[lower](cmd, setHistory, setConnectedNetwork, connectedNetwork);
      setHistory(prev => [...prev, `> ${cmd}`, ...result]);

    } else {
      // Unknown → fallback to AI
      setHistory(prev => [...prev, `> ${cmd}`, 'Processing...']);
      const aiRawResponse = await getAIResponse(cmd);
      let aiData: any = {};

      try {
        aiData = JSON.parse(aiRawResponse || '{}');
      } catch {
        aiData = { message: aiRawResponse };
      }

      const message = aiData.message || 'Assistant is currently unavailable or experiencing issues. Please type "help" to see available commands.';
      setHistory(prev => [...prev.slice(0, -1), message]);

      if (aiData.command && typeof aiData.command === 'string') {
        // Run suggested command
        await runCommand(aiData.command);
      }
    }
  };

  const handleCommand = async (cmd: string) => {
    await runCommand(cmd);
  };

  const onEnter = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const trimmed = input.trim();
      setInput('');
      if (trimmed) await handleCommand(trimmed);
    }
  };

  return (
    <div>
      <div className="window-title">Terminal</div>
      <div className="window-content">
        <div className="terminal-wrapper" onClick={() => inputRef.current?.focus()}>
          <div className="terminal">
            <div className="terminal-history" ref={historyRef}>
              {history.map((line, i) => (
                <div key={i} className="terminal-line">{line}</div>
              ))}
            </div>
            <div className="input-line">
              <span>&gt;</span>
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={onEnter}
                autoFocus
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terminal;
